/**
 * Scroll a region while a drag hovers near its edge — view ADR §11's "auto-scroll at the canvas
 * edges during a drag".
 *
 * Two regions need it and both are already scrollers built for another reason: the week pane, which
 * clips a heavy week at 1440×900, and the dock's body, which is a long day. Without it a trainer
 * dragging the last exercise of a fifteen-position day to the top has to release, scroll, and pick
 * up again — and a keyboard user never sees the position they just moved to.
 *
 * ── It is bound whether or not a drag is running ─────────────────────────────────────────────
 * `autoScrollForElements` registers a monitor and does nothing until a drag is over the element, so
 * binding it once per mounted scroller costs a listener rather than a frame. Binding it on
 * `onDragStart` instead would miss the first frames of every gesture.
 *
 * ── This is where virtualization will collide, and it is worth knowing now ───────────────────
 * View ADR §10 defers virtualization with the reason that "a drop target unmounted below the fold
 * cannot receive a drop, so auto-scroll during a drag has to mount rows ahead of the pointer". The
 * week is 7 columns and the dock a capped 20 exercises, so nothing is virtualized and the collision
 * is hypothetical — but this hook is the seam it will land on.
 */

import { useEffect, type RefObject } from 'react';

import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';

/**
 * @param axis Which way the region may scroll under a drag. `'all'` is the library's own third
 *   value (`AllowedAxis` in
 *   `@atlaskit/pragmatic-drag-and-drop-auto-scroll/dist/types/internal-types.d.ts`, read there
 *   rather than recalled) and the week pane needs it since the 07.09 handoff: the canvas scrolls
 *   sideways while the day panel is open — the week gains the panel's own width to scroll into — as
 *   well as down on a heavy week, and a card dragged toward either edge has to bring the canvas
 *   with it.
 */
export function useAutoScroll(
  ref: RefObject<HTMLElement | null>,
  axis: 'vertical' | 'horizontal' | 'all',
): void {
  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    return autoScrollForElements({ element, getAllowedAxis: () => axis });
  }, [ref, axis]);
}
